import { useEffect } from 'react';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Button from '@/trio-ui/Button';
import DatePicker from '@/trio-ui/DatePicker';
import DropDown from '@/trio-ui/Dropdown';
import Modal, { ModalTypes } from '@/trio-ui/Modal';
import TextArea from '@/trio-ui/Textarea';
import WarningMessage from '@/trio-ui/WarningMessage';
import { ValuesWithLabel } from '@/types';
import { PTORequest, PTOType } from '@/types/api/pto';
import { formatDateInterval, getFormatedDate } from '@/utils/dates';
import { requestTimeOffFormSchema } from '@/utils/formsValidation';
import {
  getBusinessDates,
  isMondayAfterHolidayOnWeekend,
  isWeekendOrFixedHoliday,
  ptoTypeLabel,
} from '@/utils/pto';
import * as Styles from './RequestTimeOffModal.styles';

export type FormData = yup.Asserts<typeof requestTimeOffFormSchema>;
const DEFAULT_HOURS = '8';

export type ConfirmationModalMode = 'created' | 'updated' | 'canceled';

export type Props = Pick<ModalTypes, 'isOpen' | 'onClose'> & {
  onSubmit?: (data: FormData) => void;
  isLoading?: boolean;
  isConfirmationOpen?: boolean;
  onConfirmationClose: () => void;
  onConfirm?: () => void;
  isSuccessModalOpen?: ConfirmationModalMode;
  onSuccessModalClose: () => void;
  initialPTOType?: PTOType;
  editing?: PTORequest;
  isDeleteConfirmationModalOpen?: boolean;
  onDeleteClick?: () => void;
  onDeleteConfirm?: () => void;
  onDeleteCancel?: () => void;
};

const quantityOptions: ValuesWithLabel[] = [
  {
    label: '4 hours',
    value: '4',
  },
  {
    label: '5 hours',
    value: '5',
  },
  {
    label: '6 hours',
    value: '6',
  },
  {
    label: '7 hours',
    value: '7',
  },
  {
    label: '8 hours',
    value: '8',
  },
];

const categoryOptions: ValuesWithLabel[] = [
  { value: PTOType.PERSONAL_PAUSE, label: 'Personal Time Off' },
  { value: PTOType.HEALTH_PAUSE, label: 'Sick Time' },
  { value: PTOType.HEALTH_LEAVE, label: 'Medical Leave' },
  { value: PTOType.GRIEF_PAUSE, label: 'Grief Time' },
  { value: PTOType.PRIMARY_CAREGIVER, label: 'Caregiver Leave' },
];

const caregiverStatusOptions: ValuesWithLabel[] = [
  {
    value: PTOType.PRIMARY_CAREGIVER,
    label: 'Primary Caregiver',
  },
  {
    value: PTOType.SECONDARY_CAREGIVER,
    label: 'Secondary Caregiver',
  },
];

const formatDateWithWeekDay = (date: Date): string => format(new Date(date), 'EEEE, MMM d, yyyy');

const getEditingCategory = (type: PTOType): PTOType => {
  if (type === PTOType.SECONDARY_CAREGIVER) {
    return PTOType.PRIMARY_CAREGIVER;
  }

  return type;
};

const getEditingCaregiverStatus = (
  type?: PTOType
): PTOType.PRIMARY_CAREGIVER | PTOType.SECONDARY_CAREGIVER | undefined => {
  if (!type) return undefined;

  if (type === PTOType.PRIMARY_CAREGIVER || type === PTOType.SECONDARY_CAREGIVER) return type;

  return undefined;
};

const RequestTimeOffModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  isConfirmationOpen = false,
  onConfirmationClose,
  onConfirm,
  isSuccessModalOpen,
  onSuccessModalClose,
  initialPTOType,
  editing,
  isDeleteConfirmationModalOpen = false,
  onDeleteClick,
  onDeleteConfirm,
  onDeleteCancel,
}: Props): JSX.Element => {
  const { handleSubmit, values, handleChange, errors, setFieldValue, touched, resetForm } =
    useFormik<yup.TypeOf<typeof requestTimeOffFormSchema>>({
      initialValues: {
        category: initialPTOType,
        caregiverStatus: getEditingCaregiverStatus(initialPTOType),
        from: undefined,
        to: undefined,
        quantities: {},
        note: '',
      },
      validateOnBlur: false,
      validationSchema: requestTimeOffFormSchema,
      onSubmit: (data) => {
        if (isLoading) return;

        onSubmit?.(data as FormData);
      },
    });

  useEffect(() => {
    resetForm();
    setFieldValue('category', editing ? getEditingCategory(editing.ptoType) : initialPTOType);
    setFieldValue(
      'caregiverStatus',
      editing
        ? getEditingCaregiverStatus(editing.ptoType)
        : getEditingCaregiverStatus(initialPTOType)
    );
    setFieldValue('from', editing ? new Date(editing.firstDay) : undefined);
    setFieldValue(
      'to',
      editing && editing.entries.length > 0
        ? new Date(editing.entries[editing.entries.length - 1].date)
        : undefined
    );
    setFieldValue(
      'quantities',
      editing
        ? editing.entries.reduce((curr, entry) => {
            return {
              ...curr,
              [getFormatedDate(new Date(entry.date))]: entry.hours,
            };
          }, {})
        : {}
    );
    setFieldValue('note', editing?.requestComment || '');
  }, [initialPTOType, setFieldValue, editing, resetForm]);

  useEffect(() => {
    if (!values.quantities) return;

    if (values.from && values.to) {
      const validInterval = getBusinessDates(values.from, values.to);
      const validKeys = validInterval.map((date) => getFormatedDate(date));
      const quantities = { ...(values.quantities || {}) };
      let updated = false;

      Object.keys(quantities).forEach((key) => {
        if (quantities && !validKeys.includes(key)) {
          delete quantities[key];
          updated = true;
        }
      });

      validKeys.forEach((key) => {
        if (!quantities[key]) {
          quantities[key] = DEFAULT_HOURS;
          updated = true;
        }
      });

      if (updated) {
        setFieldValue('quantities', quantities);
      }
    }
  }, [values.from, values.to, values.quantities, setFieldValue]);

  useEffect(() => {
    if (values.from && !values.to) {
      setFieldValue('to', values.from);
    }
  }, [values.from, values.to, setFieldValue]);

  const dateInterval = values.from && values.to ? getBusinessDates(values.from, values.to) : [];

  const intervalSum = dateInterval.reduce((sum, date) => {
    const key = getFormatedDate(date);
    const value = values.quantities?.[key] ? parseInt(values.quantities[key], 10) : 0;
    return sum + value;
  }, 0);

  const today = new Date();
  const submitButtonLabel = editing ? 'Save Edits' : 'Send request';

  const filterDates = (date: Date): boolean => {
    return !isWeekendOrFixedHoliday(date) && !isMondayAfterHolidayOnWeekend(date);
  };

  const modalBody = (
    <div>
      <form onSubmit={handleSubmit}>
        <Styles.Subtitle>Define the details for your request below.</Styles.Subtitle>
        <Styles.Label>PTO Category</Styles.Label>
        <DropDown
          isLabelAboveField
          optionsWithLabel={categoryOptions}
          name="category"
          defaultValue={values.category}
          errorMessage={(touched.category && errors.category) || undefined}
          onChangeDropDown={handleChange}
          darkerLabel
        />
        {(values.category === PTOType.PRIMARY_CAREGIVER ||
          values.category === PTOType.SECONDARY_CAREGIVER) && (
          <>
            <Styles.Label>Your Caregiver Status</Styles.Label>
            <DropDown
              isLabelAboveField
              optionsWithLabel={caregiverStatusOptions}
              name="caregiverStatus"
              defaultValue={values.caregiverStatus}
              errorMessage={(touched.caregiverStatus && errors.caregiverStatus) || undefined}
              onChangeDropDown={handleChange}
              darkerLabel
            />
          </>
        )}
        <Styles.Divider />
        <Styles.Separator>
          <div>
            <Styles.Label>From</Styles.Label>
            <Styles.InputWrapper>
              <DatePicker
                value={values.from}
                name="from"
                onChange={(date) => setFieldValue('from', date)}
                errorMessage={(touched.from && errors.from) || undefined}
                minDate={today}
                maxDate={values.to}
                filterDate={filterDates}
                className="tw-mb-4"
              />
            </Styles.InputWrapper>
          </div>
          <div>
            <Styles.Label>To</Styles.Label>
            <Styles.InputWrapper>
              <DatePicker
                value={values.to}
                name="to"
                minDate={values.from || today}
                onChange={(date) => setFieldValue('to', date)}
                errorMessage={(touched.to && errors.to) || undefined}
                className="tw-mb-4"
              />
            </Styles.InputWrapper>
          </div>
        </Styles.Separator>
        <Styles.Divider />
        {dateInterval.length > 0 && (
          <div>
            <Styles.Label>Quantity per Day</Styles.Label>
            <Styles.QuantitiesContainer>
              {dateInterval.map((date) => {
                const dateKey = getFormatedDate(date);

                return (
                  <Styles.QuantityField key={dateKey}>
                    <Styles.QuantityLabel>{formatDateWithWeekDay(date)}</Styles.QuantityLabel>
                    <DropDown
                      isLabelAboveField
                      optionsWithLabel={quantityOptions}
                      name={dateKey}
                      defaultValue={values.quantities?.[dateKey]?.toString() || undefined}
                      errorMessage={
                        touched.quantities && errors.quantities && !values.quantities?.[dateKey]
                          ? errors.quantities
                          : undefined
                      }
                      hideErrorMessage
                      onChangeDropDown={(e) => {
                        setFieldValue('quantities', {
                          ...values.quantities,
                          [dateKey]: e.target.value,
                        });
                      }}
                    />
                  </Styles.QuantityField>
                );
              })}
              <Styles.QuantityField className="relative">
                <WarningMessage
                  errorMessage={(touched.quantities && errors.quantities) || undefined}
                  style={{ bottom: 0 }}
                />
                <Styles.Label $lg>Total</Styles.Label>
                <Styles.Label $lg $center>
                  {`${intervalSum} ${intervalSum === 1 ? 'hour' : 'hours'}`}
                </Styles.Label>
              </Styles.QuantityField>
            </Styles.QuantitiesContainer>
            <Styles.Divider />
          </div>
        )}
        <Styles.Label>Note</Styles.Label>
        <TextArea name="note" value={values.note} onChange={handleChange} />
        <Styles.Footer>
          <Button type="submit" disabled={isLoading}>
            {submitButtonLabel}
          </Button>
          {editing && (
            <Button type="button" onClick={onDeleteClick} disabled={isLoading} variantColor="red">
              Cancel request
            </Button>
          )}
          <Button type="button" variantStyle="outline" onClick={onClose}>
            Cancel
          </Button>
        </Styles.Footer>
      </form>
    </div>
  );

  const categoryLabel = values.category ? ptoTypeLabel[values.category] : undefined;

  const confirmationModalBody = (
    <div>
      <Styles.ConfirmationText className="tw-mb-4">
        Please, review your request before submitting it.
      </Styles.ConfirmationText>
      <Styles.ConfirmationText>
        <strong>PTO Category</strong>
        <br />
        {((values.category === PTOType.PRIMARY_CAREGIVER ||
          values.category === PTOType.SECONDARY_CAREGIVER) &&
        values.caregiverStatus
          ? ptoTypeLabel[values.caregiverStatus]
          : categoryLabel) || '-'}
      </Styles.ConfirmationText>
      <Styles.ConfirmationSeparator />
      <Styles.Separator $lg>
        <Styles.ConfirmationText>
          <strong>Start Date</strong>
          <br />
          {values.from ? formatDateWithWeekDay(values.from) : '-'}
        </Styles.ConfirmationText>
        <Styles.ConfirmationText>
          <strong>End Date</strong>
          <br />
          {values.to ? formatDateWithWeekDay(values.to) : '-'}
        </Styles.ConfirmationText>
      </Styles.Separator>
      <Styles.ConfirmationSeparator />
      <Styles.ConfirmationText>
        <strong>Quantity per Day</strong>
      </Styles.ConfirmationText>
      {dateInterval.map((date) => {
        const key = getFormatedDate(date);
        const quantity = values.quantities?.[key] ? parseInt(values.quantities[key], 10) : 0;

        return (
          <Styles.Separator $lg key={key}>
            <Styles.ConfirmationText>{formatDateWithWeekDay(date)}</Styles.ConfirmationText>
            <Styles.ConfirmationText>
              {quantity ? `${quantity} ${quantity === 1 ? 'hour' : 'hours'}` : '-'}
            </Styles.ConfirmationText>
          </Styles.Separator>
        );
      })}
      <Styles.ConfirmationSeparator />
      <Styles.ConfirmationText>
        <strong>Note</strong>
        <br />
        {values.note || '-'}
      </Styles.ConfirmationText>
    </div>
  );

  const successBody = (
    <Styles.ConfirmationText>
      {(isSuccessModalOpen === 'created' || isSuccessModalOpen === 'updated') && (
        <>
          Great! Your PTO request has been{' '}
          {isSuccessModalOpen === 'updated' ? 'updated' : 'submitted'} successfully!
          <br />
          <br />
          You’ll receive a notification as soon as it has been reviewed.
          <br />
          <br />
          Until next time! 👋
        </>
      )}
      {isSuccessModalOpen === 'canceled' && (
        <>
          Your Paid request{' '}
          {editing?.entries && editing.entries.length > 0
            ? `for ${formatDateInterval(
                new Date(editing.firstDay),
                new Date(editing.entries[editing.entries.length - 1].date),
                'MMM d'
              )}`
            : ''}{' '}
          was successfully canceled.
        </>
      )}
    </Styles.ConfirmationText>
  );

  const modalTitle = editing ? 'Edit PTO Request' : 'Request PTO';
  const deleteModalTitle = 'Cancel Request';

  const deleteConfirmationModalBody = (
    <Styles.ConfirmationText>
      Are you sure you want to cancel this PTO request? This action cannot be undone.
    </Styles.ConfirmationText>
  );

  return (
    <>
      <Modal
        modalTitle={modalTitle}
        bodyRender={modalBody}
        isOpen={isOpen && !isConfirmationOpen && !isDeleteConfirmationModalOpen}
        onClose={onClose}
        hideCancelButton
        isCentered
      />
      <Modal
        modalTitle={modalTitle}
        bodyRender={confirmationModalBody}
        isOpen={isConfirmationOpen}
        onClose={onConfirmationClose}
        isButtonDisabled={isLoading}
        onConfirmClick={onConfirm}
        onConfirmLabel={submitButtonLabel}
        isCentered
      />
      <Modal
        modalTitle={isSuccessModalOpen === 'canceled' ? deleteModalTitle : modalTitle}
        bodyRender={successBody}
        isOpen={Boolean(isSuccessModalOpen)}
        onClose={onSuccessModalClose}
        onCancelLabel="Finish"
        onCancelButtonVariantStyle="filled"
        isCentered
      />
      <Modal
        modalTitle={deleteModalTitle}
        bodyRender={deleteConfirmationModalBody}
        isOpen={isDeleteConfirmationModalOpen}
        onClose={() => onDeleteCancel?.()}
        onConfirmClick={onDeleteConfirm}
        onCancelLabel="No, keep it"
        onConfirmLabel="Yes, cancel it"
        onConfirmButtonVariant="red"
        isCentered
        isButtonDisabled={isLoading}
        revertButtons
      />
    </>
  );
};

export default RequestTimeOffModal;
